import React from 'react';
import { LuX } from 'react-icons/lu';
import { FaTimes } from 'react-icons/fa';
import { IoMdSearch } from 'react-icons/io';
import { PiSquaresFour } from 'react-icons/pi';
import { IconType } from 'react-icons';
import type { Polygon } from 'geojson';
import { Tooltip } from 'antd';
import type { MapFeature } from '../types';
import type { PaddockMapMode } from './PaddockMapCanvas';
import { calculateAreaInHectares } from '../mapUtils';

function ic(Icon: IconType, size = 14) {
  const C = Icon as React.ComponentType<{ size?: number }>;
  return <C size={size} />;
}

function renderIcon(Icon: IconType) {
  const Component = Icon as React.ComponentType<object>;
  return <Component />;
}

function getZoneTooltip(zoneType: number, zoneName: string): string {
  const prefix = (zoneType & 1) === 1 ? 'Default' : 'Custom';
  const middle = (zoneType & 2) === 2 ? ' Cropping' : '';
  return `${prefix}${middle} Zone: ${zoneName}`;
}

/** Compute geometric area (ha, 2 dp) from a Polygon, or null if geometry is
 *  invalid / still being drawn (fewer than 3 points). */
function computeTurfArea(geometry: Polygon | null | undefined): number | null {
  if (!geometry) return null;
  const ring = geometry.coordinates?.[0];
  if (!ring || ring.length < 3) return null;
  return calculateAreaInHectares({ type: 'Feature', geometry, properties: {} });
}

/** Compute vertex and hole counts from a Polygon.
 *  GeoJSON rings are closed (first === last), so vertices = ring.length - 1. */
function computeGeomInfo(
  geometry: Polygon | null | undefined
): { vertices: number; holes: number } | null {
  const rings = geometry?.coordinates;
  if (!rings || rings.length === 0) return null;
  const vertices = (rings[0]?.length ?? 0) - 1;
  if (vertices <= 0) return null;
  return { vertices, holes: rings.length - 1 };
}

// ─────────────────────────────────────────────────────────────
// PaddockInfoPanel
// Left-side sliding panel — mirrors OpterraPhoenix's
// MapOverlaySidePanel + PaddockDetail header/body layout.
// Positioned below the top toolbar (top: 3.75rem ≈ 60px).
// ─────────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="flex-shrink-0 text-label text-charcoal">{label}</span>
      <span className="min-w-0 text-right text-label text-charcoal">{value}</span>
    </div>
  );
}

const PaddockInfoPanel = ({
  selectedPaddock,
  mode,
  draftName,
  onDraftNameChange,
  onClose,
  draftGeometry,
}: {
  selectedPaddock: MapFeature;
  mode: PaddockMapMode;
  /** Current name draft — provided only in edit/draw mode. */
  draftName: string;
  onDraftNameChange: (name: string) => void;
  onClose: () => void;
  /** Completed draw polygon — used to compute area in draw mode.
   *  Null while the user is still drawing (before the polygon is closed). */
  draftGeometry?: Polygon | null;
}) => {
  const isEditing = mode === 'edit' || mode === 'draw';
  const iconClass =
    (selectedPaddock.zoneType & 1) === 1 ? 'text-success' : 'text-chart-4';
  const unit = selectedPaddock.areaUnit ?? 'ha';

  // ── Geometry info ───────────────────────────────────────────
  const geomSource = mode === 'draw' ? draftGeometry : selectedPaddock.geometry;
  const geomInfo = computeGeomInfo(geomSource);

  // ── Area display ────────────────────────────────────────────
  let areaNode: React.ReactNode;

  if (mode === 'draw') {
    // In draw mode, show the geometric area once the polygon is closed.
    const turfArea = computeTurfArea(draftGeometry);
    areaNode = turfArea !== null ? `${turfArea} ${unit}` : '—';
  } else {
    // View / edit: compare geometric area vs stored zone.zoneArea.
    const turfArea = computeTurfArea(selectedPaddock.geometry);
    const storedArea = selectedPaddock.area;

    if (turfArea !== null && turfArea !== storedArea) {
      // Areas differ — stored value has been manually overridden.
      areaNode = (
        <span className="inline-flex items-center gap-1">
          <span>{storedArea} {unit}</span>
          <Tooltip
            title={
              <>
                <div>Geometric area: {turfArea} {unit}</div>
                <div>Stored value has been manually overridden.</div>
              </>
            }
            placement="top"
          >
            <span className="inline-flex cursor-help text-gray-muted">
              <IoMdSearch size={12} />
            </span>
          </Tooltip>
        </span>
      );
    } else {
      areaNode = turfArea !== null ? `${turfArea} ${unit}` : `${storedArea} ${unit}`;
    }
  }

  return (
    <div
      className="animate-slide-in-left absolute left-3 z-10 w-44 overflow-hidden rounded-xl bg-white map-card-shadow"
      style={{ top: '3.75rem' }}
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2">
        <div className="min-w-0 flex-1 pr-2">
          {isEditing ? (
            <>
              {/* Inline name editor — shown in edit/draw mode only */}
              <div className="relative flex items-center gap-2">
                {selectedPaddock.zoneName && (
                  <Tooltip
                    title={getZoneTooltip(
                      selectedPaddock.zoneType,
                      selectedPaddock.zoneName
                    )}
                    placement="top"
                  >
                    <span className={`flex-shrink-0 ${iconClass}`}>
                      <PiSquaresFour size={16} />
                    </span>
                  </Tooltip>
                )}
                <div className="relative flex min-w-0 flex-1 items-center">
                  <input
                    type="text"
                    value={draftName}
                    onChange={(e) => onDraftNameChange(e.target.value)}
                    className={[
                      'w-full bg-transparent text-sm font-semibold leading-tight text-charcoal',
                      'placeholder-gray-muted border-b pb-0.5 pr-5 focus:outline-none transition-colors',
                      draftName.trim()
                        ? 'border-gray-300 focus:border-gray-medium'
                        : 'border-red-400',
                    ].join(' ')}
                    placeholder="Paddock name"
                  />
                  {draftName && (
                    <button
                      type="button"
                      onClick={() => onDraftNameChange('')}
                      className="absolute right-0 text-gray-muted transition-colors hover:text-gray-dark"
                      aria-label="Clear name"
                    >
                      {ic(LuX, 13)}
                    </button>
                  )}
                </div>
              </div>
              {!draftName.trim() && (
                <p className="mt-0.5 text-xs text-red-500">Name cannot be empty.</p>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              {selectedPaddock.zoneName && (
                <Tooltip
                  title={getZoneTooltip(
                    selectedPaddock.zoneType,
                    selectedPaddock.zoneName
                  )}
                  placement="top"
                >
                  <span className={`flex-shrink-0 ${iconClass}`}>
                    <PiSquaresFour size={16} />
                  </span>
                </Tooltip>
              )}
              <Tooltip title={selectedPaddock.name} placement="top" mouseEnterDelay={0.5}>
                <p className="truncate select-none text-xs font-semibold uppercase tracking-wider text-charcoal">
                  {selectedPaddock.name}
                </p>
              </Tooltip>
            </div>
          )}
        </div>
        <Tooltip title="Deselect" placement="top">
          <button
            type="button"
            onClick={onClose}
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-gray-muted transition hover:bg-gray-200 hover:text-gray-dark"
            aria-label="Deselect"
          >
            {renderIcon(FaTimes)}
          </button>
        </Tooltip>
      </div>

      {/* ── Details ────────────────────────────────────────── */}
      <div className="px-3 py-2">
        <div className="divide-y divide-secondary-light">
          <Row label="Area" value={areaNode} />
          {geomInfo && (
            <Row
              label="Geom"
              value={
                <>
                  {geomInfo.vertices} {geomInfo.vertices === 1 ? 'vertex' : 'vertices'}
                  {geomInfo.holes > 0 && ` ${geomInfo.holes} ${geomInfo.holes === 1 ? 'hole' : 'holes'}`}
                </>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PaddockInfoPanel;
